import API_BASE, { setCsrfToken, withApiCredentials } from "../api";
import {
  clearBootstrapTokenForSource,
  getAuthBootstrapTokenCandidates,
} from "./bootstrapTokens";
import {
  clearSupabaseBrowserSession,
  getSupabaseClient,
} from "./supabaseClient";

const OAUTH_PROFILE_COMPLETION_TOKEN_KEY =
  "reelitpro.oauth.profile_completion.access_token";

function getWindowObject() {
  return typeof window !== "undefined" ? window : null;
}

function getOAuthRedirectUrl() {
  const win = getWindowObject();
  if (!win) return "/auth/callback";
  return `${win.location.origin}/auth/callback`;
}

function getErrorDetail(errorPayload) {
  const detail = errorPayload?.detail || errorPayload?.message;
  return typeof detail === "string" ? detail : "";
}

function createOAuthError(detail, fallbackMessage = "oauth_request_failed") {
  const error = new Error(detail || fallbackMessage);
  error.detail = detail || fallbackMessage;
  return error;
}

async function parseApiResponse(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw createOAuthError(getErrorDetail(payload), "oauth_request_failed");
  }

  return payload || {};
}

function getStoredSupabaseBridgeToken() {
  const win = getWindowObject();
  if (!win) return "";

  return (
    getAuthBootstrapTokenCandidates(win).find(
      (candidate) => candidate.source === "supabase"
    )?.token || ""
  );
}

function hydrateCsrfToken(payload) {
  if (typeof payload?.csrfToken === "string" && payload.csrfToken.trim()) {
    setCsrfToken(payload.csrfToken);
  }
}

function persistOAuthCompletionToken() {
  const win = getWindowObject();
  clearBootstrapTokenForSource("supabase", win);
}

export function getStoredOAuthCompletionToken() {
  return "";
}

export function clearOAuthCompletionToken() {
  const win = getWindowObject();
  if (!win) return;
  win.sessionStorage.removeItem(OAUTH_PROFILE_COMPLETION_TOKEN_KEY);
}

export async function storeAppAuthSession(sessionPayload) {
  const win = getWindowObject();
  if (!win || !sessionPayload?.user) {
    throw createOAuthError("oauth_session_invalid");
  }

  hydrateCsrfToken(sessionPayload);
  clearBootstrapTokenForSource("supabase", win);
  clearOAuthCompletionToken();
  await clearSupabaseBrowserSession({ logWarning: true });

  return sessionPayload.user;
}

export async function startGoogleOAuth() {
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: getOAuthRedirectUrl(),
    },
  });

  if (error) {
    throw createOAuthError(error.message, "oauth_start_failed");
  }
}

export async function resolveGoogleOAuthCallback() {
  const win = getWindowObject();
  const currentUrl = win ? new URL(win.location.href) : null;
  const providerError =
    currentUrl?.searchParams.get("error_description") ||
    currentUrl?.searchParams.get("error");

  if (providerError) {
    throw createOAuthError("oauth_provider_callback_failed");
  }

  const authCode = currentUrl?.searchParams.get("code") || "";
  let accessToken = "";

  if (authCode) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.exchangeCodeForSession(authCode);
    if (error) {
      throw createOAuthError(error.message, "oauth_callback_exchange_failed");
    }
    accessToken = data?.session?.access_token || "";
  }

  accessToken = accessToken || getStoredSupabaseBridgeToken();

  if (!accessToken) {
    throw createOAuthError("oauth_session_missing");
  }

  const backendSession = await requestOAuthSession(accessToken);
  if (backendSession?.profileRequired) {
    hydrateCsrfToken(backendSession);
    persistOAuthCompletionToken();
    return {
      profileRequired: true,
      email: backendSession.email || "",
    };
  }

  return {
    profileRequired: false,
    user: await storeAppAuthSession(backendSession),
  };
}

export async function requestOAuthSession(accessToken) {
  const response = await fetch(`${API_BASE}/auth/oauth/session`, withApiCredentials({
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  }));

  return parseApiResponse(response);
}

export async function completeOAuthProfile(payloadOrAccessToken, maybePayload) {
  const accessToken = maybePayload ? payloadOrAccessToken : "";
  const payload = maybePayload || payloadOrAccessToken;
  const response = await fetch(`${API_BASE}/auth/oauth/complete-profile`, withApiCredentials({
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload || {}),
  }));

  const sessionPayload = await parseApiResponse(response);
  return storeAppAuthSession(sessionPayload);
}

export async function clearOAuthCompletionSession() {
  const win = getWindowObject();
  try {
    await fetch(`${API_BASE}/auth/oauth/cancel-profile`, withApiCredentials({
      method: "POST",
    }));
  } catch {
    // Best-effort cleanup. The pending cookie is short-lived and HttpOnly.
  }
  clearOAuthCompletionToken();
  clearBootstrapTokenForSource("supabase", win);
  await clearSupabaseBrowserSession();
}
